import _ from 'lodash';

/**
 * Extracts Instagram handle from various input formats
 * @param {string} input - Instagram handle, URL, or @ mention
 * @returns {string|null} - Clean Instagram handle or null if invalid
 */
export const extractInstagramHandle = input => {
  if (!input || !_.isString(input)) {
    return null;
  }

  const trimmed = _.trim(input);

  // Check if it looks like a URL (contains protocol or instagram.com)
  const looksLikeUrl = trimmed.includes('://') ||
                       trimmed.includes('instagram.com') ||
                       trimmed.includes('www.');

  // If it doesn't look like a URL, treat it as a plain handle
  if (!looksLikeUrl) {
    // Only remove @ prefix - nothing else
    const candidate = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    const validation = validateInstagramHandle(candidate);
    return validation.isValid ? validation.handle : null;
  }

  try {
    // Add protocol if missing for URL parsing
    let urlString = trimmed;
    if (!urlString.match(/^https?:\/\//)) {
      urlString = `https://${urlString}`;
    }

    const url = new URL(urlString);

    // Check if it's an Instagram domain
    if (!_.includes(url.hostname, 'instagram.com')) {
      // If it's not Instagram, treat as plain handle - only remove @ prefix
      const candidate = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
      const validation = validateInstagramHandle(candidate);
      return validation.isValid ? validation.handle : null;
    }

    // Extract handle from Instagram URL path
    const pathParts = _.filter(_.split(url.pathname, '/'), part => _.size(part) > 0);

    if (_.size(pathParts) > 0) {
      const candidate = _.head(pathParts);
      const validation = validateInstagramHandle(candidate);
      return validation.isValid ? validation.handle : null;
    }

    return null;
  } catch (error) {
    // If URL parsing fails, treat as plain handle - only remove @ prefix
    const candidate = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    const validation = validateInstagramHandle(candidate);
    return validation.isValid ? validation.handle : null;
  }
};

/**
 * Validates Instagram handle against official Instagram rules
 * @param {string} handle - Instagram handle to validate
 * @returns {object} - Validation result with isValid, error, handle, warnings
 */
export const validateInstagramHandle = handle => {
  if (!handle || !_.isString(handle)) {
    return {
      isValid: false,
      error: 'Handle is required',
      handle: null,
    };
  }

  const cleaned = _.toLower(_.trim(handle));

  const validation = {
    isValid: true,
    error: null,
    handle: cleaned,
    warnings: [],
  };

  // Length check (1-30 characters)
  if (_.size(cleaned) < 1) {
    validation.isValid = false;
    validation.error = 'Username cannot be empty';
    return validation;
  }

  if (_.size(cleaned) > 30) {
    validation.isValid = false;
    validation.error = 'Username cannot be longer than 30 characters';
    return validation;
  }

  // Character validation - only letters, numbers, periods, and underscores
  const validCharRegex = /^[a-z0-9._]+$/;
  if (!validCharRegex.test(cleaned)) {
    validation.isValid = false;
    validation.error = 'Username can only contain letters, numbers, periods, and underscores';
    return validation;
  }

  // Cannot start or end with period
  if (_.startsWith(cleaned, '.') || _.endsWith(cleaned, '.')) {
    validation.isValid = false;
    validation.error = 'Username cannot start or end with a period';
    return validation;
  }

  // Warnings for potentially problematic handles
  if (_.includes(cleaned, '__')) {
    validation.warnings.push('Multiple consecutive underscores may look unprofessional');
  }

  if (_.size(_.split(cleaned, '.')) > 3) {
    validation.warnings.push('Many periods in username may be confusing');
  }

  return validation;
};

/**
 * Processes single-column CSV data and extracts/validates Instagram handles
 * @param {Array} csvData - Array of CSV row objects (single column)
 * @param {number} currentInfluencerCount - Current number of influencers in campaign
 * @param {number} maxInfluencers - Maximum allowed influencers per campaign
 * @param {string[]} existingCampaignHandles - Lowercase Instagram handles already on the campaign
 * @returns {object} - Processed results with validation summary
 */
export const processInfluencerCSV = (csvData, currentInfluencerCount = 0, maxInfluencers = 100, existingCampaignHandles = []) => {
  const results = _.map(csvData, (row, index) => {
    const result = { originalRow: index + 1 };

    // Get the first (and only) column value
    const firstKey = _.head(_.keys(row));
    const instagramInput = firstKey ? _.get(row, firstKey) : null;

    if (instagramInput && _.trim(String(instagramInput))) {
      const cleanInput = _.trim(String(instagramInput));
      const extractedHandle = extractInstagramHandle(cleanInput);

      if (extractedHandle) {
        const validation = validateInstagramHandle(extractedHandle);

        result.instagramHandle = validation.isValid ? validation.handle : null;
        result.originalInstagramInput = cleanInput;
        result.extractedHandle = extractedHandle;
        result.isValid = validation.isValid;
        result.validationError = validation.error;
        result.validationWarnings = validation.warnings || [];
      } else {
        result.isValid = false;
        result.validationError = `Could not extract handle from: "${cleanInput}"`;
        result.originalInstagramInput = cleanInput;
      }
    } else {
      result.isValid = false;
      result.validationError = 'Empty or missing Instagram data';
    }

    return result;
  });

  // Check for duplicates
  const validResults = _.filter(results, 'isValid');
  const handleCounts = _.countBy(validResults, 'instagramHandle');
  const duplicates = _.keys(_.pickBy(handleCounts, count => count > 1));

  _.forEach(results, result => {
    if (result.isValid && _.includes(duplicates, result.instagramHandle)) {
      result.validationWarnings.push('Duplicate handle in this CSV');
    }
  });

  // Mark handles that are already on the campaign (handles are already lowercase from validateInstagramHandle)
  const existingSet = _.isEmpty(existingCampaignHandles) ? [] : existingCampaignHandles;
  _.forEach(results, result => {
    if (result.isValid && result.instagramHandle) {
      result.alreadyInCampaign = existingSet.indexOf(result.instagramHandle) !== -1;
    }
  });

  const uniqueValidInfluencers = _.uniqBy(_.filter(results, 'isValid'), 'instagramHandle');
  const uniqueNewInfluencers = _.reject(uniqueValidInfluencers, r => r.alreadyInCampaign);
  const newToUploadCount = _.size(uniqueNewInfluencers);
  const totalAfterUpload = currentInfluencerCount + newToUploadCount;
  const availableSlots = Math.max(0, maxInfluencers - currentInfluencerCount);

  const summary = {
    total: _.size(csvData),
    valid: _.size(_.filter(results, 'isValid')),
    invalid: _.size(_.filter(results, r => !r.isValid)),
    withWarnings: _.size(_.filter(results, r => _.get(r, 'validationWarnings.length', 0) > 0)),
    duplicates: _.size(duplicates),
    overLimit: false,
    currentInfluencerCount,
    maxInfluencers,
    availableSlots,
    alreadyInCampaign: _.size(_.filter(results, r => r.isValid && r.alreadyInCampaign)),
    newToUpload: newToUploadCount,
  };

  if (totalAfterUpload > maxInfluencers) {
    summary.overLimit = true;
    summary.validAfterDedup = newToUploadCount;
    summary.totalAfterUpload = totalAfterUpload;
  }

  return { results, summary, duplicates, uniqueValidInfluencers, uniqueNewInfluencers };
};

/**
 * Test cases for Instagram handle extraction and validation
 */
export const testInstagramHandleExtraction = () => {
  const testCases = [
    'kimkardashian',
    '@kimkardashian',
    'bob.smith', // This should now work correctly
    '@bob.smith',
    'user_name',
    'https://instagram.com/kimkardashian?igsh=N3FzdmJrOHBzejhh',
    'https://instagram.com/kimkardashian/',
    'https://www.instagram.com/kimkardashian?igsh=N3FzdmJrOHBzejhh',
    'instagram.com/kimkardashian',
    'www.instagram.com/kimkardashian',
    'https://instagram.com/kimkardashian/p/somepost/',
    'invalid input',
    '',
    null,
  ];

  console.log('Instagram Handle Extraction Tests:');
  _.forEach(testCases, test => {
    const extracted = extractInstagramHandle(test);
    const validation = extracted ? validateInstagramHandle(extracted) : { isValid: false };
    console.log(`"${test}" -> "${extracted}" (valid: ${validation.isValid})`);
  });
};



/** WEBPACK FOOTER **
 ** ./src/components/csv-upload-modal/instagram-utils.js
 **/